import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Divider,
  Card,
  CardContent,
  Alert,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  styled,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  RestartAlt as RestartAltIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Palette as PaletteIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Tab as TabIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Inventory2 as PackageIcon,
  Lightbulb as LightbulbIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { 
  getProductSolutionSettings, 
  saveProductSolutionSettings,
  getAboutTabs,
  saveAboutTab,
  deleteAboutTab
} from '../../../services/ecommerceApi';
import type { CorporateHeroSectionSettings } from '../../../types/corporateHero';
import { defaultCorporateHeroSectionSettings } from '../../../types/corporateHero';
import { buildCorporateHeroSavePayload, normalizeCorporateHeroSection } from '../../../utils/corporateHeroSettings';
import SectionVisibilityToggle from './shared/SectionVisibilityToggle';
import {
  parseSectionSettingsJson,
  resolveSectionSettingsFromSource,
  serializeSectionSettingsJson,
  type SectionSettingsPayload,
} from '../utils/corporateSectionSettings';
import CorporateHeroSettingsPanel from './corporateHero/CorporateHeroSettingsPanel';
import { CustomTabSectionFields } from './customTabs/CustomTabSectionFields';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import SortableSubTabs from './SortableSubTabs';
import { buildHeroSectionOptions } from '../../../utils/corporateHeroSectionOptions';

// Visual Grid Editor'ı import et (React-Grid-Layout ile)
import HTMLEditor from './HTMLEditor';

// Debounce utility fonksiyonu
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Yeni Sekme Input Component
interface NewTabInputProps {
  onAddTab: (tabName: string) => void;
  onCancel: () => void;
}

const NewTabInput: React.FC<NewTabInputProps> = ({ onAddTab, onCancel }) => {
  const { t } = useTranslation('salesSystem');
  const [tabName, setTabName] = useState('');

  const handleSubmit = () => {
    if (tabName.trim()) {
      onAddTab(tabName.trim());
      setTabName('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Box>
      <TextField
        autoFocus
        margin="dense"
        label={t('ecommerceProductSolutionSettings.newTabInput.tabName')}
        fullWidth
        variant="outlined"
        value={tabName}
        onChange={(e) => setTabName(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={t('ecommerceProductSolutionSettings.newTabInput.placeholder')}
      />
      <DialogActions sx={{ px: 0, pt: 2 }}>
        <Button onClick={onCancel}>{t('ecommerceAboutSettings.actions.cancel')}</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={!tabName.trim()}
        >
          {t('ecommerceProductSolutionSettings.newTabInput.add')}
        </Button>
      </DialogActions>
    </Box>
  );
};

// Styled components
const StyledTabs = styled(Tabs)({
  minHeight: 'auto',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

const StyledTab = styled((props: { label: string }) => (
    <Tab disableRipple {...props} />
  ))(({ theme }) => ({
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(1, 3.5),
    minHeight: 'auto',
    minWidth: '120px',
    color: theme.palette.text.secondary,
    position: 'relative',
    backgroundColor: theme.palette.grey[200],
    clipPath: 'polygon(15% 0, 85% 0, 100% 100%, 0% 100%)',
    marginRight: theme.spacing(-2),

    '&:hover': {
        backgroundColor: theme.palette.grey[300],
    },
  
    '&.Mui-selected': {
      color: theme.palette.text.primary,
      fontWeight: theme.typography.fontWeightMedium,
      backgroundColor: theme.palette.background.paper,
      zIndex: 2,
    },
}));

// Tab paneli için yardımcı bileşen
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-solution-tabpanel-${index}`}
      aria-labelledby={`product-solution-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface ProductSolutionTabSection {
  id: string;
  title: string;
  content: string;
  image?: string;
  imagePosition?: 'left' | 'right';
  imageSize?: 'small' | 'medium' | 'large' | 'xlarge';
}

interface ProductSolutionTab {
  id: string;
  name: string;
  title: string;
  content: string;
  backgroundColor: string;
  order: number;
  active: boolean;
  sections?: ProductSolutionTabSection[];
  subTabs?: ProductSolutionTab[]; // Alt sekmeler
  navDisplayMode?: 'panel' | 'navbar' | 'both'; // Gösterim yeri: yan panel / üst navbar / ikisi
}

interface ProductSolutionSettings {
  heroSection: CorporateHeroSectionSettings;
  productsSection: {
    title: string;
    description: string;
    backgroundColor: string;
    products: Array<{
      id: string;
      title: string;
      description: string;
      icon: string;
      color: string;
    }>;
  };
  solutionsSection: {
    title: string;
    description: string;
    backgroundColor: string;
    solutions: Array<{
      id: string;
      title: string;
      description: string;
      icon: string;
      color: string;
    }>;
  };
  whyChooseSection: {
    title: string;
    description: string;
    backgroundColor: string;
    features: Array<{
      id: string;
      title: string;
      description: string;
    }>;
  };
  customTabs?: ProductSolutionTab[];
  sectionSettings: SectionSettingsPayload;
}

const defaultProductSolutionSettings: ProductSolutionSettings = {
  heroSection: {
    ...defaultCorporateHeroSectionSettings,
    title: 'Products & Solutions',
    description: 'Grow your business with tailored products and innovative solutions.',
    backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    height: 500,
  },
  productsSection: {
    title: 'Our Products',
    description: 'Meet your needs with our high-quality and reliable products',
    backgroundColor: '#ffffff',
    products: [
      {
        id: 'product-1',
        title: 'Premium Products',
        description: 'Our premium product line manufactured to the highest quality standards.',
        icon: 'package',
        color: '#3b82f6'
      },
      {
        id: 'product-2',
        title: 'Technology Products',
        description: 'Smart product solutions equipped with the latest technology.',
        icon: 'zap',
        color: '#10b981'
      },
      {
        id: 'product-3',
        title: 'Custom Design',
        description: 'Unique products specifically designed for your needs.',
        icon: 'lightbulb',
        color: '#8b5cf6'
      },
      {
        id: 'product-4',
        title: 'Security Products',
        description: 'Protection products that prioritize your safety.',
        icon: 'shield',
        color: '#f59e0b'
      }
    ]
  },
  solutionsSection: {
    title: 'Our Solutions',
    description: 'Smart solutions that optimize your business processes',
    backgroundColor: '#f9f9f9',
    solutions: [
      {
        id: 'solution-1',
        title: 'Digital Transformation',
        description: 'Comprehensive transformation solutions that prepare your business for the digital age.',
        icon: 'target',
        color: '#3b82f6'
      },
      {
        id: 'solution-2',
        title: 'Otomasyon Sistemleri',
        description: 'Smart system solutions that automate your business processes.',
        icon: 'zap',
        color: '#10b981'
      },
      {
        id: 'solution-3',
        title: 'Consulting Services',
        description: 'Strategic consulting and guidance services from our expert team.',
        icon: 'lightbulb',
        color: '#8b5cf6'
      },
      {
        id: 'solution-4',
        title: 'Teknik Destek',
        description: '7/24 teknik destek ile kesintisiz hizmet garantisi.',
        icon: 'award',
        color: '#ef4444'
      }
    ]
  },
  whyChooseSection: {
    title: 'Why Choose Our Products & Solutions?',
    description: 'Everything you need to grow your business is here',
    backgroundColor: '#ffffff',
    features: [
      {
        id: 'feature-1',
        title: 'Innovative Technology',
        description: 'Products and solutions developed with the latest technologies.'
      },
      {
        id: 'feature-2',
        title: 'Customizable',
        description: 'Fully customizable solutions according to your needs.'
      },
      {
        id: 'feature-3',
        title: 'Expert Team',
        description: 'Professional support from our specialized team.'
      },
      {
        id: 'feature-4',
        title: 'Continuous Updates',
        description: 'Our products and solutions are continuously updated and improved.'
      },
      {
        id: 'feature-5',
        title: 'Affordable Pricing',
        description: 'We offer quality products and solutions at the best prices.'
      },
      {
        id: 'feature-6',
        title: 'Fast Implementation',
        description: 'We deploy our solutions quickly.'
      }
    ]
  },
  customTabs: [],
  sectionSettings: {},
};

interface ProductSolutionSettingsComponentProps {
  companyId: string | null;
  configId: string; // ✅ ConfigId eklendi (SQL için)
  settings?: ProductSolutionSettings | null;
  onSettingsChange?: (settings: ProductSolutionSettings) => void;
}

const ProductSolutionSettingsComponent: React.FC<ProductSolutionSettingsComponentProps> = ({ 
  companyId,
  configId, // ✅ ConfigId prop'u
  settings: externalSettings, 
  onSettingsChange 
}) => {
  const { t } = useTranslation('salesSystem');
  const [settings, setSettings] = useState<ProductSolutionSettings>(defaultProductSolutionSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tabToDelete, setTabToDelete] = useState<string | null>(null);
  const [deleteTabName, setDeleteTabName] = useState<string>('');
  const [deleting, setDeleting] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<Record<string, number>>({});
  const [subTabDeleteModalOpen, setSubTabDeleteModalOpen] = useState(false);
  const [subTabToDelete, setSubTabToDelete] = useState<{parentId: string, subTabId: string, subTabName: string} | null>(null);
  const [subTabModalInfo, setSubTabModalInfo] = useState<{ open: boolean; parentTabId: string | null }>({ open: false, parentTabId: null });
  const [reorderingSubTabs, setReorderingSubTabs] = useState(false);
  const [reorderingTabs, setReorderingTabs] = useState(false);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const theme = useTheme();

  // Ayarları yükle (SQL'den)
  useEffect(() => {
    const loadSettings = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        if (externalSettings) {
          setSettings(externalSettings);
          setLoading(false);
          return;
        }

        // SQL'den yükle
        console.log('🔄 [PRODUCT SOLUTION] SQL den yükleniyor, ConfigId:', configId);
        const response = await getProductSolutionSettings(companyId, configId); // ✅ companyId eklendi
        
        if (response && response.success && response.data) {
          // SQL'den gelen veriyi frontend formatına dönüştür
          const sqlData = response.data;
          
          // Products JSON'u parse et
          let products = [];
          if (sqlData.products) {
            try {
              products = JSON.parse(sqlData.products);
            } catch (e) {
              console.warn('Products parse hatası:', e);
              products = defaultProductSolutionSettings.productsSection.products;
            }
          } else {
            products = defaultProductSolutionSettings.productsSection.products;
          }
          
          // Solutions JSON'u parse et
          let solutions = [];
          if (sqlData.solutions) {
            try {
              solutions = JSON.parse(sqlData.solutions);
            } catch (e) {
              console.warn('Solutions parse hatası:', e);
              solutions = defaultProductSolutionSettings.solutionsSection.solutions;
            }
          } else {
            solutions = defaultProductSolutionSettings.solutionsSection.solutions;
          }
          
          // Features JSON'u parse et
          let features = [];
          if (sqlData.features) {
            try {
              features = JSON.parse(sqlData.features);
            } catch (e) {
              console.warn('Features parse hatası:', e);
              features = defaultProductSolutionSettings.whyChooseSection.features;
            }
          } else {
            features = defaultProductSolutionSettings.whyChooseSection.features;
          }
          
          const loadedSettings = {
            heroSection: normalizeCorporateHeroSection(sqlData, {
              title: sqlData.heroTitle || defaultProductSolutionSettings.heroSection.title,
              description: sqlData.heroDescription || defaultProductSolutionSettings.heroSection.description,
              backgroundColor: sqlData.heroBackgroundColor || defaultProductSolutionSettings.heroSection.backgroundColor,
              height: sqlData.heroHeight || defaultProductSolutionSettings.heroSection.height,
            }),
            productsSection: {
              title: sqlData.productsSectionTitle || defaultProductSolutionSettings.productsSection.title,
              description: sqlData.productsSectionDescription || defaultProductSolutionSettings.productsSection.description,
              backgroundColor: sqlData.productsSectionBackgroundColor || defaultProductSolutionSettings.productsSection.backgroundColor,
              products: products
            },
            solutionsSection: {
              title: sqlData.solutionsSectionTitle || defaultProductSolutionSettings.solutionsSection.title,
              description: sqlData.solutionsSectionDescription || defaultProductSolutionSettings.solutionsSection.description,
              backgroundColor: sqlData.solutionsSectionBackgroundColor || defaultProductSolutionSettings.solutionsSection.backgroundColor,
              solutions: solutions
            },
            whyChooseSection: {
              title: sqlData.whyChooseSectionTitle || defaultProductSolutionSettings.whyChooseSection.title,
              description: sqlData.whyChooseSectionDescription || defaultProductSolutionSettings.whyChooseSection.description,
              backgroundColor: sqlData.whyChooseSectionBackgroundColor || defaultProductSolutionSettings.whyChooseSection.backgroundColor,
              features: features
            },
            sectionSettings: resolveSectionSettingsFromSource(sqlData),
            customTabs: [] // Dinamik sekmeler aşağıda yüklenecek
          };
          
          // ✅ Dinamik sekmeleri yükle (EcommerceConfigPages - PageType: 'ProductSolutionTab')
          try {
            const tabsResponse = await getAboutTabs(configId, 'ProductSolutionTab');
            console.log('📑 [PRODUCT SOLUTION] Dinamik sekmeler yüklendi:', tabsResponse);
            
            // ✅ Backend response yapısı: { success, message, data: [...] }
            if (tabsResponse && tabsResponse.success && tabsResponse.data && tabsResponse.data.length > 0) {
              // ✅ Ana sekmeleri ve alt sekmeleri ayır
              const allTabs = tabsResponse.data;
              const mainTabs = allTabs.filter((tab: any) => !tab.parentId || tab.level === 0);
              const subTabs = allTabs.filter((tab: any) => tab.parentId && tab.level === 1);

              console.log(`📊 [PRODUCT SOLUTION] ${mainTabs.length} ana sekme, ${subTabs.length} alt sekme bulundu`);

              // ✅ Ana sekmeleri oluştur ve alt sekmeleri ekle
              loadedSettings.customTabs = mainTabs.map((tab: any) => {
                // Sections parse et
                let parsedSections: any[] = [];
                if (tab.sections) {
                  try {
                    const parsed = JSON.parse(tab.sections);
                    // Eski format kontrolü
                    if (Array.isArray(parsed)) {
                      parsedSections = parsed;
                    } else if (parsed.sections) {
                      parsedSections = parsed.sections;
                    }
                  } catch (e) {
                    console.error('❌ [PRODUCT SOLUTION] Sections parse hatası:', e);
                  }
                }

                // Bu ana sekmeye ait alt sekmeleri bul
                const tabSubTabs = subTabs
                  .filter((st: any) => st.parentId === tab.id)
                  .map((st: any) => {
                    // Alt sekme sections parse et
                    let stParsedSections: any[] = [];
                    if (st.sections) {
                      try {
                        const parsed = JSON.parse(st.sections);
                        if (Array.isArray(parsed)) {
                          stParsedSections = parsed;
                        } else if (parsed.sections) {
                          stParsedSections = parsed.sections;
                        }
                      } catch (e) {
                        console.error('❌ [PRODUCT SOLUTION SUBTAB] Sections parse hatası:', e);
                      }
                    }

                    return {
                      id: st.id,
                      name: st.name || st.title,
                      title: st.title,
                      content: st.content || '',
                      backgroundColor: st.backgroundColor || '#ffffff',
                      order: st.displayOrder || 0,
                      active: st.isActive,
                      sections: stParsedSections
                    };
                  })
                  .sort((a: any, b: any) => a.order - b.order);
                
                return {
                  id: tab.id,
                  name: tab.name || tab.title,
                  title: tab.title,
                  content: tab.content || '',
                  backgroundColor: tab.backgroundColor || '#ffffff',
                  order: tab.displayOrder || 0,
                  active: tab.isActive,
                  sections: parsedSections,
                  subTabs: tabSubTabs,
                  navDisplayMode: tab.navDisplayMode || 'panel'
                };
              });
              console.log(`✅ [PRODUCT SOLUTION] ${mainTabs.length} ana sekme yüklendi (alt sekmelerle birlikte)`);
            } else {
              console.log('ℹ️ [PRODUCT SOLUTION] Dinamik sekme bulunamadı');
            }
          } catch (tabErr) {
            console.error('❌ [PRODUCT SOLUTION] Dinamik sekme yükleme hatası:', tabErr);
            // Hata olsa bile devam et, customTabs boş kalır
          }
          
          setSettings(loadedSettings);
          if (onSettingsChange) {
            onSettingsChange(loadedSettings);
          }
          console.log('✅ [PRODUCT SOLUTION] SQL den yükleme başarılı');
        } else {
          // Veri yoksa default kullan
          console.log('⚠️ [PRODUCT SOLUTION] Veri bulunamadı, default kullanılıyor');
          setSettings(defaultProductSolutionSettings);
          if (onSettingsChange) {
            onSettingsChange(defaultProductSolutionSettings);
          }
        }
      } catch (err) {
        console.error('❌ [PRODUCT SOLUTION] Yükleme hatası:', err);
        setError('Ayarlar yüklenirken bir hata oluştu');
        // Hata durumunda default kullan
        setSettings(defaultProductSolutionSettings);
        if (onSettingsChange) {
          onSettingsChange(defaultProductSolutionSettings);
        }
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [companyId, configId, externalSettings, onSettingsChange]); // ✅ configId eklendi

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [autoSaveTimeout]);

  // Otomatik kaydetme (debounced) - SQL'e
  const autoSave = async (settingsToSave: ProductSolutionSettings) => {
    if (!configId || !companyId) return;

    try {
      // Frontend formatını SQL formatına dönüştür
      const requestData = {
        configId: configId,
        companyId: companyId,
        ...buildCorporateHeroSavePayload(settingsToSave.heroSection),
        
        productsSectionTitle: settingsToSave.productsSection.title,
        productsSectionDescription: settingsToSave.productsSection.description,
        productsSectionBackgroundColor: settingsToSave.productsSection.backgroundColor,
        products: JSON.stringify(settingsToSave.productsSection.products),
        
        solutionsSectionTitle: settingsToSave.solutionsSection.title,
        solutionsSectionDescription: settingsToSave.solutionsSection.description,
        solutionsSectionBackgroundColor: settingsToSave.solutionsSection.backgroundColor,
        solutions: JSON.stringify(settingsToSave.solutionsSection.solutions),
        
        whyChooseSectionTitle: settingsToSave.whyChooseSection.title,
        whyChooseSectionDescription: settingsToSave.whyChooseSection.description,
        whyChooseSectionBackgroundColor: settingsToSave.whyChooseSection.backgroundColor,
        features: JSON.stringify(settingsToSave.whyChooseSection.features),
        sectionSettingsJson: serializeSectionSettingsJson(settingsToSave.sectionSettings || {}),
      };

      console.log('💾 [PRODUCT SOLUTION] SQL e otomatik kaydediliyor');
      await saveProductSolutionSettings(requestData);
      console.log('✅ [PRODUCT SOLUTION] Otomatik kaydetme başarılı');
      notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
    } catch (err) {
      console.error('❌ [PRODUCT SOLUTION] Otomatik kaydetme hatası:', err);
    }
  };

  // Debounced otomatik kaydetme
  const debouncedAutoSave = (settingsToSave: ProductSolutionSettings) => {
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    const timeout = setTimeout(() => {
      autoSave(settingsToSave);
    }, 2000); // 2 saniye bekle
    
    setAutoSaveTimeout(timeout);
  };

  // Ayarları kaydet (SQL'e)
  const handleSave = async () => {
    if (!configId || !companyId) {
      setError('Config ID veya Şirket ID bulunamadı');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Frontend formatını SQL formatına dönüştür
      const requestData = {
        configId: configId,
        companyId: companyId,
        ...buildCorporateHeroSavePayload(settings.heroSection),
        
        productsSectionTitle: settings.productsSection.title,
        productsSectionDescription: settings.productsSection.description,
        productsSectionBackgroundColor: settings.productsSection.backgroundColor,
        products: JSON.stringify(settings.productsSection.products), // JSON string
        
        // Solutions Section
        solutionsSectionTitle: settings.solutionsSection.title,
        solutionsSectionDescription: settings.solutionsSection.description,
        solutionsSectionBackgroundColor: settings.solutionsSection.backgroundColor,
        solutions: JSON.stringify(settings.solutionsSection.solutions), // JSON string
        
        // Why Choose Section
        whyChooseSectionTitle: settings.whyChooseSection.title,
        whyChooseSectionDescription: settings.whyChooseSection.description,
        whyChooseSectionBackgroundColor: settings.whyChooseSection.backgroundColor,
        features: JSON.stringify(settings.whyChooseSection.features), // JSON string
        sectionSettingsJson: serializeSectionSettingsJson(settings.sectionSettings || {}),
      };

      console.log('💾 [PRODUCT SOLUTION] SQL e kaydediliyor:', requestData);
      const response = await saveProductSolutionSettings(requestData);
      
      if (response && response.success) {
        setSuccess('Ürün & Çözümler ayarları başarıyla kaydedildi!');
        setTimeout(() => setSuccess(null), 3000);
        console.log('✅ [PRODUCT SOLUTION] Kaydetme başarılı');
      } else {
        setError('Ayarlar kaydedilirken bir hata oluştu');
      }
    } catch (err: any) {
      console.error('❌ [PRODUCT SOLUTION] Kaydetme hatası:', err);
      setError(err.response?.data?.message || 'Ayarlar kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  // Varsayılan ayarlara sıfırla
  const handleReset = () => {
    setSettings(defaultProductSolutionSettings);
    setSuccess('Ayarlar varsayılan değerlere sıfırlandı');
    setTimeout(() => setSuccess(null), 3000);
  };

  const updateHeroSection = (heroSection: CorporateHeroSectionSettings) => {
    setSettings((prev) => {
      const newSettings = {
        ...prev,
        heroSection,
      };
      if (onSettingsChange) {
        onSettingsChange(newSettings);
      }
      debouncedAutoSave(newSettings);
      return newSettings;
    });
  };

  const updateSectionSettings = (
    sectionKey: keyof SectionSettingsPayload,
    patch: Record<string, unknown>
  ) => {
    const newSettings = {
      ...settings,
      sectionSettings: {
        ...settings.sectionSettings,
        [sectionKey]: {
          ...(settings.sectionSettings?.[sectionKey] || {}),
          ...patch,
        },
      },
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings);
  };

  // Products section güncelle
  const updateProductsSection = (field: keyof typeof settings.productsSection, value: any) => {
    const newSettings = {
      ...settings,
      productsSection: {
        ...settings.productsSection,
        [field]: value
      }
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings); // ✅ Otomatik kaydet
  };

  // Ürün güncelle
  const updateProduct = (index: number, field: keyof typeof settings.productsSection.products[0], value: string) => {
    const newSettings = {
      ...settings,
      productsSection: {
        ...settings.productsSection,
        products: settings.productsSection.products.map((product, i) => 
          i === index ? { ...product, [field]: value } : product
        )
      }
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings); // ✅ Otomatik kaydet
  };

  // Solutions section güncelle
  const updateSolutionsSection = (field: keyof typeof settings.solutionsSection, value: any) => {
    const newSettings = {
      ...settings,
      solutionsSection: {
        ...settings.solutionsSection,
        [field]: value
      }
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings); // ✅ Otomatik kaydet
  };

  // Çözüm güncelle
  const updateSolution = (index: number, field: keyof typeof settings.solutionsSection.solutions[0], value: string) => {
    const newSettings = {
      ...settings,
      solutionsSection: {
        ...settings.solutionsSection,
        solutions: settings.solutionsSection.solutions.map((solution, i) => 
          i === index ? { ...solution, [field]: value } : solution
        )
      }
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings); // ✅ Otomatik kaydet
  };

  // Why choose section güncelle
  const updateWhyChooseSection = (field: keyof typeof settings.whyChooseSection, value: any) => {
    const newSettings = {
      ...settings,
      whyChooseSection: {
        ...settings.whyChooseSection,
        [field]: value
      }
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings); // ✅ Otomatik kaydet
  };

  // Özellik güncelle
  const updateFeature = (index: number, field: keyof typeof settings.whyChooseSection.features[0], value: string) => {
    const newSettings = {
      ...settings,
      whyChooseSection: {
        ...settings.whyChooseSection,
        features: settings.whyChooseSection.features.map((feat, i) => 
          i === index ? { ...feat, [field]: value } : feat
        )
      }
    };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    debouncedAutoSave(newSettings); // ✅ Otomatik kaydet
  };

  // Tab değişikliği
  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  // Ana sekme sırasını kaydet (anasayfa modülleri gibi sürükle-bırak)
  const handleTabsReorder = async (reorderedItems: { id: string; name: string }[]) => {
    const tabMap = new Map((settings.customTabs || []).map((tab) => [tab.id, tab]));
    const normalizedTabs = reorderedItems
      .map((item, index) => {
        const existing = tabMap.get(item.id);
        if (!existing) return null;
        return { ...existing, order: index };
      })
      .filter((tab): tab is ProductSolutionTab => tab !== null);

    const previousSettings = settings;
    const sortedPrevious = [...(settings.customTabs || [])].sort((a, b) => a.order - b.order);
    const activeTabId = activeTab > 0 ? sortedPrevious[activeTab - 1]?.id : null;
    const nextActiveIndex = activeTabId
      ? normalizedTabs.findIndex((tab) => tab.id === activeTabId) + 1
      : 0;

    const newSettings = {
      ...settings,
      customTabs: normalizedTabs,
    };

    setSettings(newSettings);
    if (activeTab > 0 && nextActiveIndex > 0) {
      setActiveTab(nextActiveIndex);
    }
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }

    try {
      setReorderingTabs(true);
      setError(null);
      await Promise.all(
        normalizedTabs.map((tab) =>
          saveAboutTab({
            id: tab.id,
            configId,
            companyId,
            pageType: 'ProductSolutionTab',
            name: tab.name,
            title: tab.title,
            content: tab.content || '',
            backgroundColor: tab.backgroundColor || '#ffffff',
            displayOrder: tab.order,
            isActive: tab.active,
            sections: JSON.stringify(tab.sections || []),
            navDisplayMode: tab.navDisplayMode || 'panel',
          })
        )
      );
    } catch (reorderError) {
      console.error('❌ [PRODUCT SOLUTION TAB] Sıra kaydedilirken hata:', reorderError);
      setSettings(previousSettings);
      if (onSettingsChange) {
        onSettingsChange(previousSettings);
      }
      setError(t('ecommerceProductSolutionSettings.tabs.reorderError'));
    } finally {
      setReorderingTabs(false);
    }
  };

  // Yeni sekme ekle (SQL'e kaydet)
  const handleAddTab = async (tabName: string) => {
    try {
      console.log('➕ [PRODUCT SOLUTION TAB] Yeni sekme ekleniyor:', tabName);
      
      const newTabData = {
        configId: configId,
        companyId: companyId,
        pageType: 'ProductSolutionTab', // ✅ PageType
        name: tabName,
        title: tabName,
        content: '',
        backgroundColor: '#ffffff',
        displayOrder: (settings.customTabs?.length || 0) + 1,
        isActive: true,
        sections: JSON.stringify([]), // Boş sections array
        navDisplayMode: 'panel'
      };

      const response = await saveAboutTab(newTabData);
      
      if (response && response.success) {
        console.log('✅ [PRODUCT SOLUTION TAB] SQL e kaydedildi:', response.data);
        
        // Local state'i güncelle
        const newTab: ProductSolutionTab = {
          id: response.data.id,
          name: tabName,
          title: tabName,
          content: '',
          backgroundColor: '#ffffff',
          order: (settings.customTabs?.length || 0) + 1,
          active: true,
          sections: [],
          navDisplayMode: 'panel'
        };

        const newSettings = {
          ...settings,
          customTabs: [...(settings.customTabs || []), newTab]
        };

        setSettings(newSettings);
        setSuccess(`"${tabName}" sekmesi eklendi`);
        setTimeout(() => setSuccess(null), 3000);

        if (onSettingsChange) {
          onSettingsChange(newSettings);
        }

        setActiveTab((settings.customTabs?.length || 0) + 1);
      }
    } catch (err) {
      console.error('❌ [PRODUCT SOLUTION TAB] Ekleme hatası:', err);
      setError('Sekme eklenirken bir hata oluştu');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Sekme silme başlat
  const handleDeleteTab = (tabId: string, tabName: string) => {
    setTabToDelete(tabId);
    setDeleteTabName(tabName);
    setDeleteModalOpen(true);
  };

  // Sekme silme onay (SQL'den sil)
  const confirmDeleteTab = async () => {
    if (!tabToDelete || !companyId) return;

    setDeleting(true);
    setError(null);

    try {
      console.log('🗑️ [PRODUCT SOLUTION TAB] Sekme siliniyor:', tabToDelete);
      
      // SQL'den sil
      const response = await deleteAboutTab(tabToDelete, companyId); // ✅ companyId eklendi
      
      if (response && response.success) {
        console.log('✅ [PRODUCT SOLUTION TAB] SQL den silindi');
        
        // Local state'i güncelle
        const newSettings = {
          ...settings,
          customTabs: (settings.customTabs || []).filter(tab => tab.id !== tabToDelete)
        };

        setSettings(newSettings);
        if (onSettingsChange) {
          onSettingsChange(newSettings);
        }

        setActiveTab(0);
        setDeleteModalOpen(false);
        setTabToDelete(null);
        setDeleteTabName('');
        
        setSuccess('Sekme silindi');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      console.error('❌ [PRODUCT SOLUTION TAB] Silme hatası:', error);
      setError('Sekme silinirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setDeleting(false);
    }
  };

  // Sekme silme iptal
  const cancelDeleteTab = () => {
    setDeleteModalOpen(false);
    setTabToDelete(null);
    setDeleteTabName('');
    setError(null);
  };

  // Sekme güncelle
  const updateTab = (tabId: string, field: keyof ProductSolutionTab, value: any) => {
    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId ? { ...tab, [field]: value } : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    
    // ✅ Auto-save (debounced)
    debouncedAutoSaveTab(tabId);
  };
  
  // Auto-save fonksiyonu (ana sekmeler için)
  const debouncedAutoSaveTab = React.useCallback(
    debounce(async (tabId: string) => {
      try {
        const tab = settings.customTabs?.find(t => t.id === tabId);
        if (!tab || !configId) return;
        
        console.log('💾 [PRODUCT SOLUTION TAB] Auto-save başlatıldı:', tab.name);
        
        const tabData = {
          id: tab.id,
          configId: configId,
          companyId: companyId,
          pageType: 'ProductSolutionTab',
          name: tab.name,
          title: tab.title,
          content: tab.content || '',
          backgroundColor: tab.backgroundColor || '#ffffff',
          displayOrder: tab.order,
          isActive: tab.active,
          sections: JSON.stringify(tab.sections || []), // ✅ Sadece kendi sections'ı
          parentId: null, // ✅ Ana sekme
          level: 0, // ✅ Ana sekme
          navDisplayMode: tab.navDisplayMode || 'panel'
        };
        
        const response = await saveAboutTab(tabData);
        
        if (response && response.success) {
          console.log('✅ [PRODUCT SOLUTION TAB] Auto-save başarılı');
        }
      } catch (err) {
        console.error('❌ [PRODUCT SOLUTION TAB] Auto-save hatası:', err);
      }
    }, 1000),
    [settings.customTabs, configId, companyId]
  );

  // Auto-save fonksiyonu (alt sekmeler için - AYRI SQL SATIRI)
  const debouncedAutoSaveSubTab = React.useCallback(
    debounce(async (parentTabId: string, subTabId: string) => {
      try {
        const parentTab = settings.customTabs?.find(t => t.id === parentTabId);
        const subTab = parentTab?.subTabs?.find(st => st.id === subTabId);
        if (!subTab || !configId) return;
        
        console.log('💾 [PRODUCT SOLUTION SUBTAB] Auto-save başlatıldı:', subTab.name);
        
        const tabData = {
          id: subTab.id,
          configId: configId,
          companyId: companyId,
          pageType: 'ProductSolutionTab',
          name: subTab.name,
          title: subTab.title,
          content: subTab.content || '',
          backgroundColor: subTab.backgroundColor || '#ffffff',
          displayOrder: subTab.order,
          isActive: subTab.active,
          sections: JSON.stringify(subTab.sections || []), // ✅ Sadece kendi sections'ı
          parentId: parentTabId, // ✅ Parent ID
          level: 1 // ✅ Alt sekme
        };
        
        const response = await saveAboutTab(tabData);
        
        if (response && response.success) {
          console.log('✅ [PRODUCT SOLUTION SUBTAB] Auto-save başarılı');
        }
      } catch (err) {
        console.error('❌ [PRODUCT SOLUTION SUBTAB] Auto-save hatası:', err);
      }
    }, 1000),
    [settings.customTabs, configId, companyId]
  );

  // Sekme aktif/pasif
  const toggleTabActive = (tabId: string) => {
    updateTab(tabId, 'active', !(settings.customTabs?.find(tab => tab.id === tabId)?.active || false));
  };

  // Yeni bölüm ekle
  const addNewSection = (tabId: string) => {
    const newSection: ProductSolutionTabSection = {
      id: uuidv4(),
      title: '',
      content: '',
      image: ''
    };

    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId 
          ? { ...tab, sections: [...(tab.sections || []), newSection] }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    
    // ✅ Auto-save tetikle
    debouncedAutoSaveTab(tabId);
  };

  // Bölüm sil
  const deleteSection = (tabId: string, sectionId: string) => {
    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId 
          ? { ...tab, sections: (tab.sections || []).filter(section => section.id !== sectionId) }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    
    // ✅ Auto-save tetikle
    debouncedAutoSaveTab(tabId);
  };

  // Bölüm güncelle
  const updateSection = (tabId: string, sectionId: string, field: keyof ProductSolutionTabSection, value: string) => {
    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === tabId 
          ? { 
              ...tab, 
              sections: (tab.sections || []).map(section => 
                section.id === sectionId 
                  ? { ...section, [field]: value }
                  : section
              )
            }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    
    // ✅ Auto-save tetikle
    debouncedAutoSaveTab(tabId);
  };

  // Alt sekme ekle - AYRI SQL SATIRI OLARAK
  const addSubTab = async (parentTabId: string, subTabName: string) => {
    const newSubTab: ProductSolutionTab = {
      id: uuidv4(),
      name: subTabName,
      // Ana içerikteki sayfa başlığı opsiyonel; varsayılan boş
      title: '',
      content: '',
      backgroundColor: '#ffffff',
      order: (settings.customTabs?.find(t => t.id === parentTabId)?.subTabs?.length || 0) + 1,
      active: true,
      sections: []
    };

    try {
      // ✅ SQL'e ayrı satır olarak kaydet (ParentId ve Level ile)
      const tabData = {
        id: newSubTab.id,
        configId: configId,
        companyId: companyId,
        pageType: 'ProductSolutionTab',
        name: newSubTab.name,
        title: newSubTab.title,
        content: newSubTab.content || '',
        backgroundColor: newSubTab.backgroundColor || '#ffffff',
        displayOrder: newSubTab.order,
        isActive: newSubTab.active,
        sections: JSON.stringify(newSubTab.sections || []),
        parentId: parentTabId, // ✅ Parent ID
        level: 1 // ✅ Alt sekme
      };

      console.log('💾 [PRODUCT SOLUTION SUBTAB] SQL e ayrı satır olarak kaydediliyor:', tabData);
      const response = await saveAboutTab(tabData);

      if (response && response.success) {
        // ✅ Local state'i güncelle
        const newSettings = {
          ...settings,
          customTabs: (settings.customTabs || []).map(tab => 
            tab.id === parentTabId 
              ? { 
                  ...tab, 
                  subTabs: [...(tab.subTabs || []), newSubTab]
                }
              : tab
          )
        };

        setSettings(newSettings);
        if (onSettingsChange) {
          onSettingsChange(newSettings);
        }

        setSuccess(`"${subTabName}" alt sekmesi eklendi`);
        setTimeout(() => setSuccess(null), 3000);
        console.log('✅ [PRODUCT SOLUTION SUBTAB] SQL e ayrı satır olarak kaydedildi');
      }
    } catch (err) {
      console.error('❌ [PRODUCT SOLUTION SUBTAB] Kayıt hatası:', err);
      setError('Alt sekme eklenirken hata oluştu');
    }
  };

  // Alt sekme silme başlat
  const handleDeleteSubTab = (parentTabId: string, subTabId: string, subTabName: string) => {
    setSubTabToDelete({ parentId: parentTabId, subTabId, subTabName });
    setSubTabDeleteModalOpen(true);
  };

  // Alt sekme silme onay
  const confirmDeleteSubTab = async () => {
    if (!subTabToDelete || !companyId) return; // ✅ companyId kontrolü eklendi

    setDeleting(true);
    setError(null);

    try {
      // ✅ SQL'den ayrı satırı sil
      console.log('🗑️ [PRODUCT SOLUTION SUBTAB] SQL den siliniyor:', subTabToDelete.subTabId);
      await deleteAboutTab(subTabToDelete.subTabId, companyId); // ✅ companyId eklendi

      // ✅ Local state'i güncelle
      const newSettings = {
        ...settings,
        customTabs: (settings.customTabs || []).map(tab => 
          tab.id === subTabToDelete.parentId 
            ? { 
                ...tab, 
                subTabs: (tab.subTabs || []).filter(subTab => subTab.id !== subTabToDelete.subTabId)
              }
            : tab
        )
      };

      setSettings(newSettings);
      if (onSettingsChange) {
        onSettingsChange(newSettings);
      }

      setSubTabDeleteModalOpen(false);
      setSubTabToDelete(null);
      
      setSuccess('Alt sekme silindi');
      setTimeout(() => setSuccess(null), 3000);
      console.log('✅ [PRODUCT SOLUTION SUBTAB] SQL den silindi');
      
    } catch (error) {
      console.error('Alt sekme silinirken hata:', error);
      setError('Alt sekme silinirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setDeleting(false);
    }
  };

  // Alt sekme silme iptal
  const cancelDeleteSubTab = () => {
    setSubTabDeleteModalOpen(false);
    setSubTabToDelete(null);
    setError(null);
  };

  // Alt sekme sırasını kaydet (anasayfa modülleri gibi sürükle-bırak)
  const handleSubTabsReorder = async (parentTabId: string, reorderedItems: { id: string; name: string }[]) => {
    const parentTab = settings.customTabs?.find((tab) => tab.id === parentTabId);
    if (!parentTab || !configId) return;

    const subTabMap = new Map((parentTab.subTabs || []).map((st) => [st.id, st]));
    const normalizedSubTabs = reorderedItems
      .map((item, index) => {
        const existing = subTabMap.get(item.id);
        if (!existing) return null;
        return { ...existing, order: index };
      })
      .filter((st): st is ProductSolutionTab => st !== null);

    const previousSettings = settings;
    const activeSubTabId = (parentTab.subTabs || [])[activeSubTab[parentTabId] || 0]?.id;
    const nextActiveIndex = activeSubTabId
      ? Math.max(0, normalizedSubTabs.findIndex((st) => st.id === activeSubTabId))
      : 0;

    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map((tab) =>
        tab.id === parentTabId ? { ...tab, subTabs: normalizedSubTabs } : tab
      ),
    };

    setSettings(newSettings);
    setActiveSubTab((prev) => ({ ...prev, [parentTabId]: nextActiveIndex }));
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }

    try {
      setReorderingSubTabs(true);
      setError(null);
      await Promise.all(
        normalizedSubTabs.map((subTab) =>
          saveAboutTab({
            id: subTab.id,
            configId,
            companyId,
            pageType: 'ProductSolutionTab',
            name: subTab.name,
            title: subTab.title,
            content: subTab.content || '',
            backgroundColor: subTab.backgroundColor || '#ffffff',
            displayOrder: subTab.order,
            isActive: subTab.active,
            sections: JSON.stringify(subTab.sections || []),
            parentId: parentTabId,
            level: 1,
          })
        )
      );
    } catch (reorderError) {
      console.error('❌ [PRODUCT SOLUTION SUBTAB] Sıra kaydedilirken hata:', reorderError);
      setSettings(previousSettings);
      if (onSettingsChange) {
        onSettingsChange(previousSettings);
      }
      setError(t('ecommerceProductSolutionSettings.subTabs.reorderError'));
    } finally {
      setReorderingSubTabs(false);
    }
  };

  // Alt sekme güncelle - AYRI SQL SATIRINI GÜNCELLE
  const updateSubTab = (parentTabId: string, subTabId: string, field: keyof ProductSolutionTab, value: any) => {
    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === parentTabId 
          ? { 
              ...tab, 
              subTabs: (tab.subTabs || []).map(subTab => 
                subTab.id === subTabId 
                  ? { ...subTab, [field]: value }
                  : subTab
              )
            }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    
    // ✅ Auto-save tetikle (alt sekmeyi ayrı satır olarak kaydet)
    debouncedAutoSaveSubTab(parentTabId, subTabId);
  };

  // Alt sekme bölüm ekle
  const addNewSubTabSection = (parentTabId: string, subTabId: string) => {
    const newSection: ProductSolutionTabSection = {
      id: uuidv4(),
      title: '',
      content: '',
      image: ''
    };

    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === parentTabId 
          ? { 
              ...tab, 
              subTabs: (tab.subTabs || []).map(subTab => 
                subTab.id === subTabId 
                  ? { ...subTab, sections: [...(subTab.sections || []), newSection] }
                  : subTab
              )
            }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    
    // ✅ Auto-save tetikle (alt sekmeyi ayrı satır olarak kaydet)
    debouncedAutoSaveSubTab(parentTabId, subTabId);
  };

  // Alt sekme bölüm sil
  const deleteSubTabSection = (parentTabId: string, subTabId: string, sectionId: string) => {
    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === parentTabId 
          ? { 
              ...tab, 
              subTabs: (tab.subTabs || []).map(subTab => 
                subTab.id === subTabId 
                  ? { ...subTab, sections: (subTab.sections || []).filter(section => section.id !== sectionId) }
                  : subTab
              )
            }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    
    // ✅ Auto-save tetikle (alt sekmeyi ayrı satır olarak kaydet)
    debouncedAutoSaveSubTab(parentTabId, subTabId);
  };

  // Alt sekme bölüm güncelle
  const updateSubTabSection = (parentTabId: string, subTabId: string, sectionId: string, field: keyof ProductSolutionTabSection, value: string) => {
    const newSettings = {
      ...settings,
      customTabs: (settings.customTabs || []).map(tab => 
        tab.id === parentTabId 
          ? { 
              ...tab, 
              subTabs: (tab.subTabs || []).map(subTab => 
                subTab.id === subTabId 
                  ? { 
                      ...subTab, 
                      sections: (subTab.sections || []).map(section => 
                        section.id === sectionId 
                          ? { ...section, [field]: value }
                          : section
                      )
                    }
                  : subTab
              )
            }
          : tab
      )
    };

    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
    
    // ✅ Auto-save tetikle (alt sekmeyi ayrı satır olarak kaydet)
    debouncedAutoSaveSubTab(parentTabId, subTabId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const customTabs = (settings.customTabs || []).sort((a, b) => a.order - b.order);

  return (
    <Paper elevation={3} sx={{ position: 'relative', zIndex: 1 }}>
      {/* Tab Header */}
      <Box sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pt: 1,
          px: 1,
          backgroundColor: theme.palette.grey[200],
      }}>
          <SortableSubTabs
            generalLabel={t('ecommerceProductSolutionSettings.tabs.settings')}
            tabs={customTabs.map((tab) => ({ id: tab.id, name: tab.name }))}
            activeIndex={activeTab}
            reordering={reorderingTabs}
            dragHandleTitle={t('ecommerceProductSolutionSettings.tabs.dragToReorder')}
            ariaLabel={t('ecommerceProductSolutionSettings.tabs.ariaLabel')}
            onSelectTab={handleTabChange}
            onReorder={handleTabsReorder}
          />
          {reorderingTabs && (
            <CircularProgress size={18} sx={{ ml: 1, flexShrink: 0 }} />
          )}
          <Tooltip title={t('ecommerceAboutSettings.tabs.addNewTab')}>
              <IconButton
                size="small"
                sx={{ ml: 1, flexShrink: 0 }}
                onClick={() => setIsModalOpen(true)}
                disabled={reorderingTabs}
              >
                  <AddIcon />
              </IconButton>
          </Tooltip>
      </Box>

      {/* Ana Ürün & Çözümler Ayarları Tab */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            <PackageIcon sx={{ mr: 1.5, color: 'primary.main' }} />
            {t('ecommerceProductSolutionSettings.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('ecommerceAboutSettings.actions.resetToDefaultSettings')}>
              <IconButton onClick={handleReset} color="secondary">
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !companyId}
            >
              {saving ? t('ecommerceProductSolutionSettings.actions.saving') : t('ecommerceProductSolutionSettings.actions.save')}
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Hero Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
              {t('ecommerceProductSolutionSettings.sections.hero')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CorporateHeroSettingsPanel
              value={settings.heroSection}
              onChange={updateHeroSection}
              companyId={companyId}
              defaultHeight={500}
              sectionOptions={buildHeroSectionOptions(
                [
                  {
                    label: settings.productsSection?.title || 'Ürünler',
                    value: 'products-products',
                  },
                  {
                    label: settings.solutionsSection?.title || 'Çözümler',
                    value: 'products-solutions',
                  },
                  {
                    label: settings.whyChooseSection?.title || 'Neden Biz',
                    value: 'products-why-choose',
                  },
                  { label: 'SSS', value: 'products_solutions_faq' },
                ],
                settings.customTabs
              )}
            />
          </AccordionDetails>
        </Accordion>

        {/* Products Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <PackageIcon sx={{ mr: 1, color: 'primary.main' }} />
              {t('ecommerceProductSolutionSettings.sections.products')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SectionVisibilityToggle
                  enabled={settings.sectionSettings?.productsSection?.enabled !== false}
                  onChange={(enabled) => updateSectionSettings('productsSection', { enabled })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('ecommerceAboutSettings.fields.title')}
                  value={settings.productsSection.title}
                  onChange={(e) => updateProductsSection('title', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={t('ecommerceAboutSettings.fields.description')}
                  value={settings.productsSection.description}
                  onChange={(e) => updateProductsSection('description', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('ecommerceAboutSettings.fields.backgroundColor')}
                  value={settings.productsSection.backgroundColor}
                  onChange={(e) => updateProductsSection('backgroundColor', e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={settings.productsSection.backgroundColor}
                          onChange={(e) => updateProductsSection('backgroundColor', e.target.value)}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('ecommerceProductSolutionSettings.sections.productsList')}
                </Typography>
              </Grid>
              
              {settings.productsSection.products.map((product, index) => (
                <Grid item xs={12} key={product.id}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label={t('ecommerceAboutSettings.fields.title')}
                          value={product.title}
                          onChange={(e) => updateProduct(index, 'title', e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label={t('ecommerceAboutSettings.fields.description')}
                          value={product.description}
                          onChange={(e) => updateProduct(index, 'description', e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label={t('ecommerceAboutSettings.fields.color')}
                          value={product.color}
                          onChange={(e) => updateProduct(index, 'color', e.target.value)}
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  component="input"
                                  type="color"
                                  value={product.color}
                                  onChange={(e) => updateProduct(index, 'color', e.target.value)}
                                  sx={{ border: 'none', width: '20px', height: '20px', p: 0, background: 'none', cursor: 'pointer' }}
                                />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Solutions Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <LightbulbIcon sx={{ mr: 1, color: 'primary.main' }} />
              {t('ecommerceProductSolutionSettings.sections.solutions')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SectionVisibilityToggle
                  enabled={settings.sectionSettings?.solutionsSection?.enabled !== false}
                  onChange={(enabled) => updateSectionSettings('solutionsSection', { enabled })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('ecommerceAboutSettings.fields.title')}
                  value={settings.solutionsSection.title}
                  onChange={(e) => updateSolutionsSection('title', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={t('ecommerceAboutSettings.fields.description')}
                  value={settings.solutionsSection.description}
                  onChange={(e) => updateSolutionsSection('description', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('ecommerceAboutSettings.fields.backgroundColor')}
                  value={settings.solutionsSection.backgroundColor}
                  onChange={(e) => updateSolutionsSection('backgroundColor', e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={settings.solutionsSection.backgroundColor}
                          onChange={(e) => updateSolutionsSection('backgroundColor', e.target.value)}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('ecommerceProductSolutionSettings.sections.solutionsList')}
                </Typography>
              </Grid>
              
              {settings.solutionsSection.solutions.map((solution, index) => (
                <Grid item xs={12} key={solution.id}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label={t('ecommerceAboutSettings.fields.title')}
                          value={solution.title}
                          onChange={(e) => updateSolution(index, 'title', e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label={t('ecommerceAboutSettings.fields.description')}
                          value={solution.description}
                          onChange={(e) => updateSolution(index, 'description', e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label={t('ecommerceAboutSettings.fields.color')}
                          value={solution.color}
                          onChange={(e) => updateSolution(index, 'color', e.target.value)}
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  component="input"
                                  type="color"
                                  value={solution.color}
                                  onChange={(e) => updateSolution(index, 'color', e.target.value)}
                                  sx={{ border: 'none', width: '20px', height: '20px', p: 0, background: 'none', cursor: 'pointer' }}
                                />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Why Choose Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ mr: 1, color: 'primary.main' }} />
              {t('ecommerceAboutSettings.sections.whyChooseUs')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SectionVisibilityToggle
                  enabled={settings.sectionSettings?.whyChooseSection?.enabled !== false}
                  onChange={(enabled) => updateSectionSettings('whyChooseSection', { enabled })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('ecommerceAboutSettings.fields.title')}
                  value={settings.whyChooseSection.title}
                  onChange={(e) => updateWhyChooseSection('title', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={t('ecommerceAboutSettings.fields.description')}
                  value={settings.whyChooseSection.description}
                  onChange={(e) => updateWhyChooseSection('description', e.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('ecommerceAboutSettings.fields.backgroundColor')}
                  value={settings.whyChooseSection.backgroundColor}
                  onChange={(e) => updateWhyChooseSection('backgroundColor', e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={settings.whyChooseSection.backgroundColor}
                          onChange={(e) => updateWhyChooseSection('backgroundColor', e.target.value)}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('ecommerceAboutSettings.sections.features')}
                </Typography>
              </Grid>
              
              {settings.whyChooseSection.features.map((feature, index) => (
                <Grid item xs={12} md={6} key={feature.id}>
                  <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t('ecommerceAboutSettings.fields.title')}
                          value={feature.title}
                          onChange={(e) => updateFeature(index, 'title', e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label={t('ecommerceAboutSettings.fields.description')}
                          value={feature.description}
                          onChange={(e) => updateFeature(index, 'description', e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </TabPanel>

      {/* Özel Sekme Tab'ları */}
      {customTabs.map((tab, index) => (
        <TabPanel key={tab.id} value={activeTab} index={index + 1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              <TabIcon sx={{ mr: 1.5, color: 'primary.main' }} />
              {t('ecommerceAboutSettings.customTabs.tabSettingsTitle', { tabName: tab.name })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('ecommerceAboutSettings.customTabs.deleteThisTab')}>
                <IconButton 
                  onClick={() => handleDeleteTab(tab.id, tab.name)} 
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Accordion defaultExpanded sx={{ mb: 3, boxShadow: 'none', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                <SettingsIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                {t('ecommerceProductSolutionSettings.customTabs.mainTabSettingsContent')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Genel Ayarlar */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceServiceSettings.customTabs.tabNameVisibleSidebar')}
                    value={tab.name}
                    onChange={(e) => updateTab(tab.id, 'name', e.target.value)}
                    variant="outlined"
                    placeholder="Technology Products"
                    helperText={t('ecommerceServiceSettings.customTabs.tabNameHelper')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceAboutSettings.customTabs.pageTitle')}
                    value={tab.title}
                    onChange={(e) => updateTab(tab.id, 'title', e.target.value)}
                    variant="outlined"
                    placeholder="Technology Products and Solutions"
                    helperText={t('ecommerceServiceSettings.customTabs.pageTitleHelper')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('ecommerceAboutSettings.fields.backgroundColor')}
                    value={tab.backgroundColor}
                    onChange={(e) => updateTab(tab.id, 'backgroundColor', e.target.value)}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="input"
                            type="color"
                            value={tab.backgroundColor}
                            onChange={(e) => updateTab(tab.id, 'backgroundColor', e.target.value)}
                            sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body2" sx={{ mr: 2 }}>{t('ecommerceAboutSettings.customTabs.status')}</Typography>
                    <Button
                      variant={tab.active ? "contained" : "outlined"}
                      color={tab.active ? "success" : "warning"}
                      onClick={() => toggleTabActive(tab.id)}
                      startIcon={tab.active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    >
                      {tab.active ? t('ecommerceAboutSettings.customTabs.active') : t('ecommerceAboutSettings.customTabs.hidden')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {/* Ana İçerik - Visual Grid Editör */}
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {t('ecommerceProductSolutionSettings.customTabs.mainTabContentVisualEditor')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceProductSolutionSettings.customTabs.mainTabContentHelper')}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? t('ecommerceProductSolutionSettings.actions.saving') : t('ecommerceProductSolutionSettings.actions.save')}
                </Button>
              </Box>
              <HTMLEditor
                key={`tab-editor-${tab.id}`}
                value={tab.content || ''}
                onChange={(html: string) => {
                  // ✅ Local state'i güncelle VE otomatik kaydetmeyi tetikle
                  const newSettings = {
                    ...settings,
                    customTabs: (settings.customTabs || []).map(t => 
                      t.id === tab.id ? { ...t, content: html } : t
                    )
                  };
                  setSettings(newSettings);
                  if (onSettingsChange) {
                    onSettingsChange(newSettings);
                  }
                  // ✅ Auto-save tetikle
                  debouncedAutoSaveTab(tab.id);
                }}
                placeholder={t('ecommerceProductSolutionSettings.customTabs.mainTabContentPlaceholder')}
                companyId={companyId}
              />
            </AccordionDetails>
          </Accordion>
          
          <Grid container spacing={3}>
            {/* Alt Sekmeler */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  📂 {t('ecommerceProductSolutionSettings.subTabs.titleWithCount', { count: (tab.subTabs || []).length })}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setSubTabModalInfo({ open: true, parentTabId: tab.id });
                  }}
                  size="small"
                >
                  {t('ecommerceProductSolutionSettings.subTabs.add')}
                </Button>
              </Box>

              {(tab.subTabs || []).length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('ecommerceProductSolutionSettings.subTabs.empty')}
                </Alert>
              ) : (
                <Paper elevation={2} sx={{ position: 'relative', zIndex: 1, backgroundColor: '#f8f9fa' }}>
                  {/* Alt Sekme Tab Header */}
                  <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: 1,
                      borderColor: 'divider',
                      pt: 1,
                      px: 1,
                      backgroundColor: theme.palette.grey[200],
                  }}>
                      <SortableSubTabs
                        tabs={(tab.subTabs || []).map((subTab) => ({
                          id: subTab.id,
                          name: subTab.name,
                        }))}
                        activeIndex={activeSubTab[tab.id] || 0}
                        reordering={reorderingSubTabs}
                        dragHandleTitle={t('ecommerceProductSolutionSettings.subTabs.dragToReorder')}
                        unnamedLabel={t('ecommerceProductSolutionSettings.subTabs.unnamed')}
                        ariaLabel={t('ecommerceProductSolutionSettings.subTabs.ariaLabel')}
                        onSelectTab={(index) =>
                          setActiveSubTab((prev) => ({ ...prev, [tab.id]: index }))
                        }
                        onReorder={(reordered) => handleSubTabsReorder(tab.id, reordered)}
                      />
                      {reorderingSubTabs && (
                        <CircularProgress size={18} sx={{ ml: 1, flexShrink: 0 }} />
                      )}
                  </Box>

                  {/* Alt Sekme Tab Panelleri */}
                  {(tab.subTabs || []).map((subTab, subTabIndex) => (
                    <TabPanel key={subTab.id} value={activeSubTab[tab.id] || 0} index={subTabIndex}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" fontWeight="bold">
                          <TabIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                          {t('ecommerceProductSolutionSettings.subTabs.settingsTitle', { subTabName: subTab.name })}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={t('ecommerceProductSolutionSettings.subTabs.deleteThisSubTab')}>
                            <IconButton 
                              onClick={() => handleDeleteSubTab(tab.id, subTab.id, subTab.name)} 
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            required
                            label={t('ecommerceProductSolutionSettings.subTabs.nameVisibleSidebar')}
                            value={subTab.name}
                            onChange={(e) => updateSubTab(tab.id, subTab.id, 'name', e.target.value)}
                            variant="outlined"
                            placeholder="Hızlı Satış"
                            helperText={t('ecommerceProductSolutionSettings.subTabs.nameRequiredHelper')}
                            error={!subTab.name?.trim()}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={t('ecommerceProductSolutionSettings.subTabs.pageTitleOptional')}
                            value={subTab.title}
                            onChange={(e) => updateSubTab(tab.id, subTab.id, 'title', e.target.value)}
                            variant="outlined"
                            placeholder={t('ecommerceProductSolutionSettings.subTabs.pageTitlePlaceholder')}
                            helperText={t('ecommerceProductSolutionSettings.subTabs.pageTitleOptionalHelper')}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={t('ecommerceAboutSettings.fields.backgroundColor')}
                            value={subTab.backgroundColor}
                            onChange={(e) => updateSubTab(tab.id, subTab.id, 'backgroundColor', e.target.value)}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box
                                    component="input"
                                    type="color"
                                    value={subTab.backgroundColor}
                                    onChange={(e) => updateSubTab(tab.id, subTab.id, 'backgroundColor', e.target.value)}
                                    sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                                  />
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2">{t('ecommerceAboutSettings.customTabs.status')}</Typography>
                            <Button
                              variant={subTab.active ? "contained" : "outlined"}
                              color={subTab.active ? "success" : "warning"}
                              onClick={() => updateSubTab(tab.id, subTab.id, 'active', !subTab.active)}
                              startIcon={subTab.active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            >
                              {subTab.active ? t('ecommerceAboutSettings.customTabs.active') : t('ecommerceAboutSettings.customTabs.hidden')}
                            </Button>
                          </Box>
                        </Grid>
                        
                        {/* Ana İçerik - Visual Grid Editör */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                            {t('ecommerceProductSolutionSettings.subTabs.mainContentVisualEditor')}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              {t('ecommerceProductSolutionSettings.subTabs.mainContentHelper')}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<SaveIcon />}
                              onClick={handleSave}
                              disabled={saving}
                            >
                              {saving ? t('ecommerceProductSolutionSettings.actions.saving') : t('ecommerceProductSolutionSettings.actions.save')}
                            </Button>
                          </Box>
                          <HTMLEditor
                            key={`subtab-editor-${tab.id}-${subTab.id}`}
                            value={subTab.content || ''}
                            onChange={(html: string) => {
                              // ✅ Local state'i güncelle VE otomatik kaydetmeyi tetikle
                              const newSettings = {
                                ...settings,
                                customTabs: (settings.customTabs || []).map(t => 
                                  t.id === tab.id 
                                    ? { 
                                        ...t, 
                                        subTabs: (t.subTabs || []).map(st => 
                                          st.id === subTab.id 
                                            ? { ...st, content: html }
                                            : st
                                        )
                                      }
                                    : t
                                )
                              };
                              setSettings(newSettings);
                              if (onSettingsChange) {
                                onSettingsChange(newSettings);
                              }
                              // ✅ Auto-save tetikle (alt sekmeyi ayrı satır olarak kaydet)
                              debouncedAutoSaveSubTab(tab.id, subTab.id);
                            }}
                            placeholder={t('ecommerceProductSolutionSettings.subTabs.mainContentPlaceholder')}
                            companyId={companyId}
                          />
                        </Grid>

                        {/* Dinamik Bölümler */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold">
                              📝 {t('ecommerceAboutSettings.customTabs.contentSectionsWithCount', { count: (subTab.sections || []).length })}
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={() => addNewSubTabSection(tab.id, subTab.id)}
                              size="small"
                            >
                              {t('ecommerceAboutSettings.customTabs.addNewSection')}
                            </Button>
                          </Box>

                          {(subTab.sections || []).length === 0 ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                              {t('ecommerceAboutSettings.customTabs.noSections')}
                            </Alert>
                          ) : (
                            <Grid container spacing={2}>
                              {(subTab.sections || []).map((section, sectionIndex) => (
                                <Grid item xs={12} key={section.id}>
                                  <Card variant="outlined" sx={{ p: 2, position: 'relative' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                      <Typography variant="subtitle2" fontWeight="bold">
                                        {t('ecommerceAboutSettings.customTabs.sectionWithNumber', { number: sectionIndex + 1 })}
                                      </Typography>
                                      <Tooltip title={t('ecommerceAboutSettings.customTabs.deleteThisSection')}>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => deleteSubTabSection(tab.id, subTab.id, section.id)}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                    
                                    <CustomTabSectionFields
                                      section={section}
                                      onUpdate={(field, value) =>
                                        updateSubTabSection(tab.id, subTab.id, section.id, field, value)
                                      }
                                      titlePlaceholder={t('ecommerceProductSolutionSettings.customTabs.sectionTitlePlaceholder')}
                                      contentPlaceholder={t('ecommerceProductSolutionSettings.customTabs.sectionContentPlaceholder')}
                                    />
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </Grid>
                      </Grid>

                      {/* Durum Göstergesi */}
                      <Box sx={{ mt: 3, p: 2, backgroundColor: subTab.active ? '#e8f5e8' : '#fff3cd', borderRadius: 1 }}>
                        <Typography variant="body2" color={subTab.active ? 'success.main' : 'warning.main'}>
                          {subTab.active ? t('ecommerceProductSolutionSettings.subTabs.activeInfo') : t('ecommerceProductSolutionSettings.subTabs.hiddenInfo')}
                        </Typography>
                      </Box>
                    </TabPanel>
                  ))}
                </Paper>
              )}
            </Grid>

            {/* Dinamik Bölümler */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  📝 {t('ecommerceAboutSettings.customTabs.contentSectionsWithCount', { count: (tab.sections || []).length })}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => addNewSection(tab.id)}
                  size="small"
                >
                  {t('ecommerceAboutSettings.customTabs.addNewSection')}
                </Button>
              </Box>

              {(tab.sections || []).length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('ecommerceAboutSettings.customTabs.noSections')}
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {(tab.sections || []).map((section, sectionIndex) => (
                    <Grid item xs={12} key={section.id}>
                      <Card variant="outlined" sx={{ p: 2, position: 'relative' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {t('ecommerceAboutSettings.customTabs.sectionWithNumber', { number: sectionIndex + 1 })}
                          </Typography>
                          <Tooltip title={t('ecommerceAboutSettings.customTabs.deleteThisSection')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteSection(tab.id, section.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        
                        <CustomTabSectionFields
                          section={section}
                          onUpdate={(field, value) => updateSection(tab.id, section.id, field, value)}
                          titlePlaceholder={t('ecommerceProductSolutionSettings.customTabs.sectionTitlePlaceholder')}
                          contentPlaceholder={t('ecommerceProductSolutionSettings.customTabs.sectionContentPlaceholder')}
                        />
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* Durum Göstergesi */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: tab.active ? '#e8f5e8' : '#fff3cd', borderRadius: 1 }}>
            <Typography variant="body2" color={tab.active ? 'success.main' : 'warning.main'}>
              {tab.active ? t('ecommerceProductSolutionSettings.customTabs.activeInfo') : t('ecommerceProductSolutionSettings.customTabs.hiddenInfo')}
            </Typography>
          </Box>
        </TabPanel>
      ))}

      {/* Yeni Sekme Ekleme Modal'ı */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('ecommerceAboutSettings.modals.addNewTabTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('ecommerceProductSolutionSettings.modals.addNewTabDescription')}
          </DialogContentText>
          <NewTabInput 
            onAddTab={(tabName) => {
              handleAddTab(tabName);
              setIsModalOpen(false);
            }}
            onCancel={() => setIsModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Sekme Silme Onay Modal'ı */}
      <Dialog
        open={deleteModalOpen}
        onClose={!deleting ? cancelDeleteTab : undefined}
        aria-labelledby="delete-tab-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-tab-dialog-title">
          Sekmeyi Sil
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            "<strong>{deleteTabName}</strong>" sekmesini silmek istediğinizden emin misiniz?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: 'warning.main', fontWeight: 'medium' }}>
            ⚠️ Bu işlem geri alınamaz. Sekmenin tüm içeriği kalıcı olarak silinecektir.
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={cancelDeleteTab} 
            disabled={deleting}
            color="primary"
          >
            İptal
          </Button>
          <Button 
            onClick={confirmDeleteTab} 
            disabled={deleting}
            color="error"
            variant="contained"
            autoFocus
          >
            {deleting ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alt Sekme Silme Onay Modal'ı */}
      <Dialog
        open={subTabDeleteModalOpen}
        onClose={!deleting ? cancelDeleteSubTab : undefined}
        aria-labelledby="delete-subtab-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-subtab-dialog-title">
          Alt Sekmeyi Sil
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            "<strong>{subTabToDelete?.subTabName}</strong>" alt sekmesini silmek istediğinizden emin misiniz?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: 'warning.main', fontWeight: 'medium' }}>
            ⚠️ Bu işlem geri alınamaz. Alt sekmenin tüm içeriği ve bölümleri kalıcı olarak silinecektir.
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={cancelDeleteSubTab} 
            disabled={deleting}
            color="primary"
          >
            İptal
          </Button>
          <Button 
            onClick={confirmDeleteSubTab} 
            disabled={deleting}
            color="error"
            variant="contained"
            autoFocus
          >
            {deleting ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Yeni Alt Sekme Ekleme Modal'ı */}
      <Dialog
        open={subTabModalInfo.open}
        onClose={() => setSubTabModalInfo({ open: false, parentTabId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Yeni Alt Sekme Ekle</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            "<strong>{customTabs.find(t => t.id === subTabModalInfo.parentTabId)?.name || ''}</strong>" sekmesine yeni bir alt sekme ekleyin.
          </DialogContentText>
          <NewTabInput 
            onAddTab={(subTabName) => {
              if (subTabModalInfo.parentTabId) {
                const parentTab = settings.customTabs?.find(t => t.id === subTabModalInfo.parentTabId);
                const newSubTabIndex = parentTab?.subTabs?.length || 0;

                addSubTab(subTabModalInfo.parentTabId, subTabName);
                
                // Yeni eklenen alt sekmeyi aktif yap
                setActiveSubTab(prev => ({
                  ...prev,
                  [subTabModalInfo.parentTabId!]: newSubTabIndex
                }));
              }
              setSubTabModalInfo({ open: false, parentTabId: null });
            }}
            onCancel={() => setSubTabModalInfo({ open: false, parentTabId: null })}
          />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default ProductSolutionSettingsComponent;
